import React, { useEffect, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { StaticPage } from '../editor/ObjectRenderer';
import { customTemplatePayload } from '../../lib/templates';
import { createCustomTemplate, apiErrorMessage } from '../../lib/projectApi';
import { renderPageToCanvas } from '../../lib/renderUtils';
import { SCHEME_PRESETS } from '../../lib/colors';
import { BookmarkPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** Longest side of the card thumbnail (px). ~360 px JPEG keeps each card under ~40 KB. */
const THUMB_SIZE = 360;

/** Snapshot of page 1 as a small JPEG data URL, or null when rendering fails (the card then renders live). */
const makeThumbnail = async (page, master, textFlow) => {
  try {
    const scale = Math.min(1, THUMB_SIZE / Math.max(page.width, page.height));
    const canvas = await renderPageToCanvas(page, master, textFlow, scale);
    return canvas.toDataURL('image/jpeg', 0.82);
  } catch (e) {
    console.warn('[SaveAsTemplate] thumbnail failed', e?.message || e); // eslint-disable-line no-console
    return null;
  }
};

/**
 * File ▸ Save As ▸ Save as template: stores the open publication (pages, masters, guides, colour scheme) as a reusable
 * custom template in the backend so it shows up in File ▸ New ▸ My templates on every browser. A small snapshot of page 1
 * is stored with it so the gallery loads instantly, however large the publication.
 */
export const SaveAsTemplateDialog = ({ open, onOpenChange, onSaved }) => {
  const { state } = useEditor();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('');

  useEffect(() => {
    if (open) { setName(state.projectName || 'My template'); setDescription(''); }
  }, [open, state.projectName]);

  const first = state.pages[0];
  const previewScale = first ? Math.min(120 / first.width, 150 / first.height) : 0;
  const schemeName = state.schemeId ? state.schemeId.charAt(0).toUpperCase() + state.schemeId.slice(1) : 'Office';
  const schemeColours = (SCHEME_PRESETS[state.schemeId] || SCHEME_PRESETS.office || []).slice(0, 6);

  const save = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      setStage('Rendering the preview…');
      const thumbnail = first ? await makeThumbnail(first, state.masters?.[first.masterId || 'default'], state.textFlow) : null;
      setStage('Saving…');
      const tpl = await createCustomTemplate({ ...customTemplatePayload(state, { name: n, description: description.trim() }), thumbnail });
      toast.success(`Saved “${tpl.name}” to My templates`, { description: 'Find it under File ▸ New ▸ My templates.' });
      onOpenChange(false);
      onSaved && onSaved(tpl);
    } catch (e) {
      toast.error(await apiErrorMessage(e, 'Could not save the template.'));
    } finally { setBusy(false); setStage(''); }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <AlertDialogContent className="z-[100] sm:max-w-[520px] bg-card text-card-foreground border-border shadow-xl" overlayClassName="z-[95] bg-black/40 backdrop-blur-sm" data-testid="save-as-template-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-outfit flex items-center gap-2"><BookmarkPlus className="h-5 w-5 text-primary" /> Save as template</AlertDialogTitle>
          <AlertDialogDescription>
            Every page, master page and guide of this publication is saved as a reusable template. When you start from it later you can pick another colour scheme — fonts and layout stay exactly as designed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid sm:grid-cols-[132px_1fr] gap-4">
          <div className="flex flex-col items-center gap-2">
            {first && (
              <div className="bg-white shadow-md ring-1 ring-black/10 overflow-hidden" style={{ width: Math.round(first.width * previewScale), height: Math.round(first.height * previewScale) }} data-testid="save-as-template-preview">
                <StaticPage page={first} master={state.masters?.[first.masterId || 'default']} scale={previewScale} textFlow={state.textFlow} />
              </div>
            )}
            <div className="text-[11px] text-muted-foreground text-center" data-testid="save-as-template-meta">{state.pages.length} page{state.pages.length > 1 ? 's' : ''} · {(first?.width / 96).toFixed(2)} × {(first?.height / 96).toFixed(2)} in</div>
            <div className="flex items-center gap-1" title={`Saved with the ${schemeName} colour scheme`} data-testid="save-as-template-scheme">
              {schemeColours.map((c, i) => <span key={`${c.hex}-${i}`} className="h-3 w-3 rounded-sm ring-1 ring-black/10" style={{ background: c.hex }} />)}
            </div>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-name">Template name</Label>
              <Input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} maxLength={120} autoFocus data-testid="save-as-template-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-desc">Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea id="tpl-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={500} placeholder="What is this template for? e.g. Monthly club newsletter, 4 pages" data-testid="save-as-template-description" />
            </div>
          </div>
        </div>
        <AlertDialogFooter className="sm:items-center">
          {stage && <span className="text-xs text-muted-foreground mr-auto" data-testid="save-as-template-stage">{stage}</span>}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="save-as-template-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !name.trim() || !first} data-testid="save-as-template-save">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BookmarkPlus className="h-4 w-4 mr-2" />} Save template</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
